
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import { collectErrors, errorMessage } from '@/components/settings/identityIntegrations/integrationErrors';
import {
  CANONICAL_FIELD_COMMUNITY_REFERENCE,
  CANONICAL_FIELD_EMAIL,
  CANONICAL_FIELD_EXTERNAL_SUBJECT,
  COMMUNITY_RESOLUTION_ATTRIBUTE_MAPPING,
  emailUseOf,
  identityIntegrationsService,
  IDENTITY_INTEGRATION_PROTOCOL_OIDC,
  IDENTITY_INTEGRATION_STATUS_ACTIVE,
  IIdentityAttributeMapping,
  IIdentityIntegration,
  isSubjectSource,
} from '@/services/identityIntegrationsService';

type Tone = 'done' | 'attention' | 'blocked' | 'next' | 'pending';

interface IStep {
  key: string;
  tone: Tone;
  detail: string;
  issues: string[];
  section: string | null;
}

const TONE_ICONS: { [tone: string]: string } = {
  done: 'mdi-check-circle',
  attention: 'mdi-alert-circle-outline',
  blocked: 'mdi-close-circle-outline',
  next: 'mdi-arrow-right-circle-outline',
  pending: 'mdi-circle-outline',
};

const TONE_COLORS: { [tone: string]: string } = {
  done: 'success',
  attention: 'warning',
  blocked: 'error',
  next: 'primary',
  pending: 'grey',
};

/**
 * The answer to "will this integration work, and if not, what is left to do",
 * laid out as the steps an operator actually goes through: connect the
 * provider, say who a user is, say where they land, switch it on.
 *
 * Each step is derived from the integration we already hold, so it can say
 * WHAT is configured rather than only whether something is. Whether it can be
 * enabled still comes from the read-only /validation endpoint, which runs the
 * very same rules enabling does — this view never blocks on a rule of its own.
 *
 * What it adds on top are warnings: things that make sign-in fail or do less
 * than intended although enabling is allowed. An empty scope (nobody can sign
 * in), no usable email (nobody is linked or created — for OIDC, an email from
 * any claim but `email` counts as none), JIT that cannot create anyone (no
 * usable email, or fixed resolution over several Communities), and an
 * attribute-mapped resolution with no Community reference mapped. A step with
 * a warning keeps the verdict from saying the configuration is complete.
 */
@Component({ name: 'IntegrationSummary' })
export default class IntegrationSummary extends Vue {
  @Prop({ required: true })
  protected integration!: IIdentityIntegration;

  protected loading: boolean = true;
  protected issues: string[] = [];
  protected loadError: string | null = null;
  private request: number = 0;

  created() {
    this.reload();
  }

  @Watch('integration')
  onIntegrationChanged() {
    this.reload();
  }

  get isActive(): boolean {
    return this.integration.status === IDENTITY_INTEGRATION_STATUS_ACTIVE;
  }

  get isOidc(): boolean {
    return this.integration.protocol === IDENTITY_INTEGRATION_PROTOCOL_OIDC;
  }

  get steps(): IStep[] {
    return [this.connectionStep(), this.identityStep(), this.communitiesStep(), this.activationStep()];
  }

  /**
   * An active integration with issues is a live incident — logins are failing
   * right now. An inactive one is simply not ready yet. Same rules, very
   * different urgency, so they do not get the same wording or colour.
   */
  get verdictTone(): Tone {
    if (this.loading || this.loadError) {
      return 'pending';
    }

    if (this.issues.length) {
      return this.isActive ? 'blocked' : 'attention';
    }

    if (this.hasWarnings) {
      return 'attention';
    }

    return this.isActive ? 'done' : 'next';
  }

  get hasWarnings(): boolean {
    return this.steps.some((step) => step.tone === 'attention');
  }

  get healthTitle(): string {
    if (this.loading) {
      return this.$t('identityIntegrations.summary.checking') as string;
    }

    // "No issues" and "could not ask" are not the same answer. Reporting a
    // failed check as a clean bill of health is the one thing this panel must
    // never do — an operator would read it as permission to stop looking.
    if (this.loadError) {
      return this.$t('identityIntegrations.summary.checkFailed') as string;
    }

    if (!this.issues.length && this.hasWarnings) {
      return this.isActive
        ? (this.$t('identityIntegrations.summary.activeWithWarnings') as string)
        : (this.$t('identityIntegrations.summary.enableableWithWarnings') as string);
    }

    if (!this.issues.length) {
      return this.isActive
        ? (this.$t('identityIntegrations.summary.healthyActive') as string)
        : (this.$t('identityIntegrations.summary.readyToEnable') as string);
    }

    return this.isActive
      ? (this.$t('identityIntegrations.summary.brokenActive') as string)
      : (this.$t('identityIntegrations.summary.notReady') as string);
  }

  get verdictHint(): string {
    if (this.loading) {
      return '';
    }

    if (this.loadError) {
      return this.loadError;
    }

    if (this.issues.length || this.hasWarnings) {
      return this.$t('identityIntegrations.setup.fixMarked') as string;
    }

    return this.isActive
      ? (this.$t('identityIntegrations.summary.healthyActiveHint') as string)
      : (this.$t('identityIntegrations.summary.readyToEnableHint') as string);
  }

  toneIcon(tone: Tone): string {
    return TONE_ICONS[tone];
  }

  toneColor(tone: Tone): string {
    return TONE_COLORS[tone];
  }

  issueLabel(issue: string): string {
    const key = 'identityIntegrations.errors.' + issue;

    return this.$te(key) ? (this.$t(key) as string) : issue;
  }

  // Every save reloads the verdict; only the answer to the latest request
  // may land, or a slow earlier one would overwrite a newer result.
  async reload() {
    const request = ++this.request;

    this.loading = true;
    this.loadError = null;
    try {
      const validation = await identityIntegrationsService.validation(this.integration.id);

      if (request === this.request) {
        this.issues = validation.issues || [];
      }
    } catch (error) {
      if (request === this.request) {
        this.issues = [];
        this.loadError = errorMessage(this, collectErrors(error)[0]);
      }
    } finally {
      if (request === this.request) {
        this.loading = false;
      }
    }
  }

  private connectionStep(): IStep {
    const missing = this.isOidc ? this.missingOidcPieces() : this.missingSamlPieces();

    if (missing.length) {
      return this.step('connection', 'attention', this.missingDetail(missing), 'connection');
    }

    const detail = this.isOidc && this.integration.oidc
      ? this.$t('identityIntegrations.setup.connection.oidcDone', [this.integration.oidc.issuer, this.integration.oidc.client_id])
      : this.$t('identityIntegrations.setup.connection.samlDone', [this.integration.issuer]);

    return this.step('connection', 'done', detail as string, 'connection');
  }

  private missingSamlPieces(): string[] {
    const missing: string[] = [];

    if (!this.integration.issuer) {
      missing.push('issuer');
    }
    if (!this.integration.idp_sso_url) {
      missing.push('ssoUrl');
    }
    if (!this.integration.certificate.configured) {
      missing.push('certificate');
    } else if (!this.integration.certificate.usable) {
      missing.push('certificateInvalid');
    }
    if (!this.integration.sp_entity_id) {
      missing.push('spEntityId');
    }

    return missing;
  }

  private missingOidcPieces(): string[] {
    const oidc = this.integration.oidc;

    if (!oidc) {
      return ['oidcProvider'];
    }

    return oidc.client_secret_configured ? [] : ['clientSecret'];
  }

  private identityStep(): IStep {
    const subject = this.sourceOf(this.mappingFor(CANONICAL_FIELD_EXTERNAL_SUBJECT)) || this.protocolSubject();
    const email = this.sourceOf(this.mappingFor(CANONICAL_FIELD_EMAIL));
    const use = emailUseOf(this.integration);

    // Not an activation rule: without a usable email the integration still
    // signs in users who are already linked. But it can never link or create
    // anybody new, which is rarely what was intended — so it is flagged, not
    // blocked.
    if (use === 'unmapped') {
      return this.step('identity', 'attention', this.$t('identityIntegrations.setup.identity.noEmail', [subject]) as string, 'identity');
    }

    if (use === 'unverifiable') {
      return this.step('identity', 'attention', this.$t('identityIntegrations.setup.identity.emailUnverifiable', [subject, email]) as string, 'identity');
    }

    const key = this.isOidc ? 'identityIntegrations.setup.identity.doneOidc' : 'identityIntegrations.setup.identity.done';

    return this.step('identity', 'done', this.$t(key, [subject, email]) as string, 'identity');
  }

  private communitiesStep(): IStep {
    const scope = this.integration.community_ids.length;
    const attributeMapped = this.integration.community_resolution.mode === COMMUNITY_RESOLUTION_ATTRIBUTE_MAPPING;
    const mappings = this.integration.community_resolution.mapping_count;

    if (scope === 0) {
      return this.step('communities', 'attention', this.$t('identityIntegrations.setup.communities.empty') as string, 'identity');
    }

    if (attributeMapped && !this.mappingFor(CANONICAL_FIELD_COMMUNITY_REFERENCE)) {
      return this.step('communities', 'attention', this.$t('identityIntegrations.setup.communities.noReference') as string, 'identity');
    }

    if (attributeMapped && mappings === 0) {
      return this.step('communities', 'attention', this.$t('identityIntegrations.setup.communities.noMappings') as string, 'identity');
    }

    // Fixed resolution creates a new user in the one community in scope; with
    // several there is no way to tell which, so JIT has nowhere to put them.
    if (!attributeMapped && this.integration.jit_provisioning_enabled && scope > 1) {
      return this.step('communities', 'attention', this.$t('identityIntegrations.setup.communities.jitNeedsOne', [scope]) as string, 'identity');
    }

    const resolution = attributeMapped
      ? this.$tc('identityIntegrations.setup.communities.mapped', scope, { mappings, scope })
      : this.$tc('identityIntegrations.setup.communities.fixed', scope, { scope });

    // JIT creates users from a usable email only (ADR-0011, ADR-0017).
    if (this.integration.jit_provisioning_enabled && emailUseOf(this.integration) !== 'usable') {
      return this.step('communities', 'attention', resolution + ' ' + this.$t('identityIntegrations.setup.communities.jitWithoutEmail'), 'identity');
    }

    const jit = this.$t(this.integration.jit_provisioning_enabled
      ? 'identityIntegrations.setup.communities.jitOn'
      : 'identityIntegrations.setup.communities.jitOff');

    return this.step('communities', 'done', resolution + ' ' + jit, 'identity');
  }

  private activationStep(): IStep {
    if (this.loading) {
      return this.step('activation', 'pending', this.$t('identityIntegrations.summary.checking') as string, null);
    }

    if (this.loadError) {
      return this.step('activation', 'pending', this.$t('identityIntegrations.summary.checkFailed') as string, null);
    }

    if (this.issues.length) {
      const blocked = this.isActive
        ? this.step('activation', 'blocked', this.$t('identityIntegrations.setup.activation.broken') as string, null)
        : this.step('activation', 'attention', this.$t('identityIntegrations.setup.activation.blocked') as string, null);
      blocked.issues = this.issues;

      return blocked;
    }

    return this.isActive
      ? this.step('activation', 'done', this.$t('identityIntegrations.setup.activation.active') as string, null)
      : this.step('activation', 'next', this.$t('identityIntegrations.setup.activation.ready') as string, null);
  }

  private missingDetail(missing: string[]): string {
    const pieces = missing.map((piece) => this.$t('identityIntegrations.setup.pieces.' + piece) as string);

    return this.$t('identityIntegrations.setup.missing', [pieces.join(', ')]) as string;
  }

  private step(key: string, tone: Tone, detail: string, section: string | null): IStep {
    return { key, tone, detail, issues: [], section };
  }

  private mappingFor(field: string): IIdentityAttributeMapping | null {
    const found = (this.integration.identity_mapping.attributes || []).filter((each) => each.canonical_field === field);

    return found.length ? found[0] : null;
  }

  // Where a field is read from, in the provider's own vocabulary: the claim or
  // attribute name, or the protocol's subject (NameID / `sub`).
  private sourceOf(mapping: IIdentityAttributeMapping | null): string | null {
    if (!mapping) {
      return null;
    }

    return isSubjectSource(mapping.source_type) ? this.protocolSubject() : mapping.source_name;
  }

  private protocolSubject(): string {
    return this.$t(this.isOidc ? 'identityIntegrations.setup.identity.sub' : 'identityIntegrations.setup.identity.nameId') as string;
  }
}
